import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Play,
  Pause,
  RotateCcw,
  Package,
  Cog,
  CheckCircle2,
  Clock,
  ArrowRight,
  AlertTriangle,
  Layers,
  Wrench,
} from "lucide-react";

interface Process {
  id: string;
  name: string;
  sequence_order: number;
  estimated_duration_minutes?: number;
  requires_machine: boolean;
  requires_mold: boolean;
}

interface SimulationStep {
  process: Process;
  status: "pending" | "in_progress" | "completed";
  startTime?: Date;
  endTime?: Date;
  progress: number;
}

export default function ProductionFlowSimulation() {
  const [isRunning, setIsRunning] = useState(false);
  const [simulationSpeed, setSimulationSpeed] = useState(1);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [steps, setSteps] = useState<SimulationStep[]>([]);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Cargar procesos de manufactura
  const { data: processes } = useQuery({
    queryKey: ["manufacturing_processes_simulation"],
    queryFn: async () => {
      const { data, error } = await api
        .from("manufacturing_processes")
        .select("*")
        .eq("is_active", true)
        .order("sequence_order");
      if (error) throw error;
      return data as Process[];
    },
  });

  // Cargar estadísticas de órdenes
  const { data: orderStats } = useQuery({
    queryKey: ["order_stats"],
    queryFn: async () => {
      const { data, error } = await api
        .from("work_orders")
        .select("status, id");
      if (error) throw error;
      const stats = {
        total: data?.length || 0,
        pending: data?.filter((o) => o.status === "pending").length || 0,
        in_progress: data?.filter((o) => o.status === "in_progress").length || 0,
        completed: data?.filter((o) => o.status === "completed").length || 0,
      };
      return stats;
    },
  });

  // Cargar recursos
  const { data: resources } = useQuery({
    queryKey: ["production_resources"],
    queryFn: async () => {
      const [machines, molds, materials] = await Promise.all([
        api.from("machines").select("id, status").eq("is_active", true),
        api.from("molds").select("id, status"),
        api.from("material_inventory").select("id, category").eq("is_active", true),
      ]);
      return {
        machines: machines.data?.length || 0,
        machinesActive: machines.data?.filter((m) => m.status === "active").length || 0,
        molds: molds.data?.length || 0,
        moldsAvailable: molds.data?.filter((m) => m.status === "available").length || 0,
        materials: materials.data?.length || 0,
      };
    },
  });

  // Inicializar pasos de simulación
  useEffect(() => {
    if (processes && processes.length > 0) {
      const defaultDurations: Record<string, number> = {
        "Plotter (Corte)": 15,
        material_arrival: 5,
        freezer_storage: 10,
        material_cutting: 20,
        mold_selection: 10,
        Laminado: 80,
        material_layup: 60,
        vacuum_bagging: 30,
        autoclave_curing: 180,
        demolding: 20,
        quality_inspection: 15,
        cutting_workshop: 45,
        packaging: 10,
        dispatch: 5,
      };

      const initialSteps: SimulationStep[] = processes.map((p) => ({
        process: {
          ...p,
          estimated_duration_minutes:
            p.estimated_duration_minutes || defaultDurations[p.name] || 30,
        },
        status: "pending",
        progress: 0,
      }));
      setSteps(initialSteps);
    }
  }, [processes]);

  // Simulación
  useEffect(() => {
    if (!isRunning || steps.length === 0) return;

    const interval = setInterval(() => {
      setSteps((prevSteps) => {
        const newSteps = [...prevSteps];
        const currentStep = newSteps[currentStepIndex];

        if (!currentStep) {
          setIsRunning(false);
          return prevSteps;
        }

        if (currentStep.status === "pending") {
          currentStep.status = "in_progress";
          currentStep.startTime = new Date();
        }

        if (currentStep.status === "in_progress") {
          const duration = currentStep.process.estimated_duration_minutes || 30;
          const increment = (100 / duration) * simulationSpeed;
          currentStep.progress = Math.min(100, currentStep.progress + increment);

          if (currentStep.progress >= 100) {
            currentStep.status = "completed";
            currentStep.endTime = new Date();

            if (currentStepIndex < newSteps.length - 1) {
              setCurrentStepIndex((prev) => prev + 1);
            } else {
              setIsRunning(false);
            }
          }
        }

        return newSteps;
      });

      setElapsedTime((prev) => prev + simulationSpeed);
    }, 1000);

    return () => clearInterval(interval);
  }, [isRunning, currentStepIndex, simulationSpeed, steps.length]);

  const resetSimulation = () => {
    setIsRunning(false);
    setCurrentStepIndex(0);
    setElapsedTime(0);
    setSteps((prev) =>
      prev.map((s) => ({
        ...s,
        status: "pending",
        progress: 0,
        startTime: undefined,
        endTime: undefined,
      }))
    );
  };

  const totalEstimatedTime = steps.reduce(
    (acc, s) => acc + (s.process.estimated_duration_minutes || 30),
    0
  );

  const completedTime = steps
    .filter((s) => s.status === "completed")
    .reduce((acc, s) => acc + (s.process.estimated_duration_minutes || 30), 0);

  const overallProgress = totalEstimatedTime > 0 ? (completedTime / totalEstimatedTime) * 100 : 0;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case "in_progress":
        return <Cog className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-500";
      case "in_progress":
        return "bg-blue-500";
      default:
        return "bg-muted";
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-4 md:p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              Simulación de Flujo de Producción
            </h1>
            <p className="text-muted-foreground mt-1">
              Visualización en tiempo real del proceso de manufactura
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Select
              value={String(simulationSpeed)}
              onValueChange={(v) => setSimulationSpeed(Number(v))}
            >
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0.5">0.5x</SelectItem>
                <SelectItem value="1">1x</SelectItem>
                <SelectItem value="2">2x</SelectItem>
                <SelectItem value="5">5x</SelectItem>
                <SelectItem value="10">10x</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant={isRunning ? "secondary" : "default"}
              onClick={() => setIsRunning(!isRunning)}
            >
              {isRunning ? (
                <>
                  <Pause className="w-4 h-4 mr-2" /> Pausar
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" /> Iniciar
                </>
              )}
            </Button>

            <Button variant="outline" onClick={resetSimulation}>
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900">
                  <Package className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{orderStats?.total || 0}</p>
                  <p className="text-xs text-muted-foreground">Órdenes Totales</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900">
                  <Cog className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{resources?.machines || 0}</p>
                  <p className="text-xs text-muted-foreground">Máquinas</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900">
                  <Wrench className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{resources?.molds || 0}</p>
                  <p className="text-xs text-muted-foreground">Moldes</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900">
                  <Layers className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{resources?.materials || 0}</p>
                  <p className="text-xs text-muted-foreground">Materiales</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Progress Overview */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center justify-between">
              <span>Progreso General</span>
              <span className="text-sm font-normal text-muted-foreground">
                {Math.round(overallProgress)}% completado
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Progress value={overallProgress} className="h-3" />
            <div className="flex justify-between mt-2 text-sm text-muted-foreground">
              <span>Tiempo simulado: {Math.round(elapsedTime)} min</span>
              <span>Tiempo total estimado: {totalEstimatedTime} min</span>
            </div>
          </CardContent>
        </Card>

        {/* Flow Visualization */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Flujo de Procesos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative">
              {/* Process Steps */}
              <div className="flex flex-wrap gap-2">
                {steps.map((step, index) => (
                  <div key={step.process.id} className="flex items-center">
                    <div
                      className={`
                        relative p-3 rounded-lg border-2 transition-all duration-300 min-w-[140px]
                        ${step.status === "in_progress" ? "border-blue-500 bg-blue-50 dark:bg-blue-950 shadow-lg scale-105" : ""}
                        ${step.status === "completed" ? "border-green-500 bg-green-50 dark:bg-green-950" : ""}
                        ${step.status === "pending" ? "border-muted bg-muted/30" : ""}
                      `}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        {getStatusIcon(step.status)}
                        <span className="font-medium text-sm truncate">
                          {step.process.name}
                        </span>
                      </div>

                      {step.status === "in_progress" && (
                        <Progress value={step.progress} className="h-1.5" />
                      )}

                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-muted-foreground">
                          {step.process.estimated_duration_minutes} min
                        </span>
                        {step.process.requires_machine && (
                          <Badge variant="outline" className="text-[10px] px-1">
                            <Cog className="w-2.5 h-2.5 mr-0.5" />
                            Máq
                          </Badge>
                        )}
                        {step.process.requires_mold && (
                          <Badge variant="outline" className="text-[10px] px-1">
                            <Wrench className="w-2.5 h-2.5 mr-0.5" />
                            Molde
                          </Badge>
                        )}
                      </div>
                    </div>

                    {index < steps.length - 1 && (
                      <ArrowRight
                        className={`w-5 h-5 mx-1 flex-shrink-0 ${
                          step.status === "completed"
                            ? "text-green-500"
                            : "text-muted-foreground"
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Process Details Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Detalle de Procesos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3">#</th>
                    <th className="text-left py-2 px-3">Proceso</th>
                    <th className="text-left py-2 px-3">Duración Est.</th>
                    <th className="text-left py-2 px-3">Requisitos</th>
                    <th className="text-left py-2 px-3">Estado</th>
                    <th className="text-left py-2 px-3">Progreso</th>
                  </tr>
                </thead>
                <tbody>
                  {steps.map((step, index) => (
                    <tr
                      key={step.process.id}
                      className={`border-b ${
                        step.status === "in_progress" ? "bg-blue-50 dark:bg-blue-950" : ""
                      }`}
                    >
                      <td className="py-2 px-3">{index + 1}</td>
                      <td className="py-2 px-3 font-medium">{step.process.name}</td>
                      <td className="py-2 px-3">
                        {step.process.estimated_duration_minutes} min
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex gap-1">
                          {step.process.requires_machine && (
                            <Badge variant="secondary" className="text-xs">
                              Máquina
                            </Badge>
                          )}
                          {step.process.requires_mold && (
                            <Badge variant="secondary" className="text-xs">
                              Molde
                            </Badge>
                          )}
                          {!step.process.requires_machine &&
                            !step.process.requires_mold && (
                              <span className="text-muted-foreground">-</span>
                            )}
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        <Badge
                          variant={
                            step.status === "completed"
                              ? "default"
                              : step.status === "in_progress"
                              ? "secondary"
                              : "outline"
                          }
                        >
                          {step.status === "completed" && "Completado"}
                          {step.status === "in_progress" && "En Proceso"}
                          {step.status === "pending" && "Pendiente"}
                        </Badge>
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <Progress value={step.progress} className="h-2 w-20" />
                          <span className="text-xs">{Math.round(step.progress)}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
